import * as React from 'react';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { KindIcon, KIND_LABEL } from '@/components/kind-icon';
import { api } from '@/lib/api';
import { detectTimeZone } from '@/lib/tz';

const VALID_KINDS = ['ectopic', 'reflux', 'ibs', 'pain'] as const;
type Kind = (typeof VALID_KINDS)[number];

const KIND_LINE_COLOR: Record<Kind, string> = {
  ectopic: '#f43f5e',
  reflux: '#f59e0b',
  ibs: '#10b981',
  pain: '#8b5cf6',
};

const RANGES = [
  { label: '4w', days: 28 },
  { label: '12w', days: 84 },
  { label: '6mo', days: 183 },
  { label: '1y', days: 365 },
] as const;

export const Route = createFileRoute('/trends/$kind')({
  beforeLoad: ({ params }) => {
    if (!VALID_KINDS.includes(params.kind as Kind)) throw notFound();
  },
  component: TrendsKindPage,
});

function TrendsKindPage() {
  const { kind } = Route.useParams();
  const tz = React.useMemo(detectTimeZone, []);
  const [days, setDays] = React.useState<number>(84);
  const k = kind as Kind;

  const series = useQuery({
    queryKey: ['series', k, days, tz],
    queryFn: () => api.getSeries(k, days, tz),
    staleTime: 60_000,
  });

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-2">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <KindIcon kind={k} className="h-6 w-6 text-muted-foreground" />
          {KIND_LABEL[k]} trends
        </h1>
        <p className="text-sm text-muted-foreground">
          Daily count with rolling 7-day and 28-day averages.
        </p>
        <div className="flex flex-wrap gap-1">
          {RANGES.map((r) => (
            <Button
              key={r.label}
              size="sm"
              variant={r.days === days ? 'default' : 'outline'}
              onClick={() => setDays(r.days)}
            >
              {r.label}
            </Button>
          ))}
          <KindSwitcher current={k} />
        </div>
      </section>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Events per day
          </CardTitle>
          <CardDescription>
            Bar = raw count. Lines = rolling means (smoothed trend).
          </CardDescription>
        </CardHeader>
        <CardContent className="h-64">
          {series.isPending && (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}
          {series.error && (
            <p className="text-sm text-destructive">
              {(series.error as Error).message}
            </p>
          )}
          {series.data && <TrendChart data={series.data.points} kind={k} />}
        </CardContent>
      </Card>

      {series.data && <SummaryCard data={series.data.points} kind={k} />}
    </div>
  );
}

function KindSwitcher({ current }: { current: Kind }) {
  return (
    <div className="ml-auto flex gap-1">
      {VALID_KINDS.filter((k) => k !== current).map((k) => (
        <Button key={k} size="sm" variant="ghost" asChild>
          <a href={`/trends/${k}`} className="inline-flex items-center gap-1">
            <KindIcon kind={k} className="h-3.5 w-3.5" />
            {KIND_LABEL[k]}
          </a>
        </Button>
      ))}
    </div>
  );
}

function TrendChart({
  data,
  kind,
}: {
  data: { date: string; count: number; rolling_7d: number; rolling_28d: number }[];
  kind: Kind;
}) {
  const color = KIND_LINE_COLOR[kind];
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: -16 }}>
        <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="date"
          stroke="hsl(var(--muted-foreground))"
          fontSize={10}
          interval="preserveStartEnd"
          tickFormatter={(v: string) => v.slice(5)}
        />
        <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} allowDecimals={false} />
        <Tooltip
          contentStyle={{
            background: 'hsl(var(--background))',
            border: '1px solid hsl(var(--border))',
            borderRadius: 6,
            fontSize: 12,
          }}
          labelStyle={{ color: 'hsl(var(--foreground))' }}
          formatter={(value: number, name: string) => [
            typeof value === 'number' ? value.toFixed(2).replace(/\.00$/, '') : String(value),
            name,
          ]}
        />
        <Line
          type="monotone"
          dataKey="count"
          name="count"
          stroke={color}
          strokeWidth={1}
          dot={{ r: 2 }}
          activeDot={{ r: 4 }}
          opacity={0.5}
        />
        <Line
          type="monotone"
          dataKey="rolling_7d"
          name="7d avg"
          stroke={color}
          strokeWidth={2}
          dot={false}
        />
        <Line
          type="monotone"
          dataKey="rolling_28d"
          name="28d avg"
          stroke="hsl(var(--muted-foreground))"
          strokeWidth={2}
          strokeDasharray="4 4"
          dot={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

function SummaryCard({
  data,
  kind,
}: {
  data: { count: number; avg_severity: number | null; rolling_7d: number; rolling_28d: number }[];
  kind: Kind;
}) {
  const total = data.reduce((a, p) => a + p.count, 0);
  const peak = data.reduce((m, p) => (p.count > m ? p.count : m), 0);
  const days = data.length;
  const latest7 = data.at(-1)?.rolling_7d ?? 0;
  const latest28 = data.at(-1)?.rolling_28d ?? 0;
  const severities = data
    .map((p) => p.avg_severity)
    .filter((v): v is number => v != null);
  const meanSev =
    severities.length === 0
      ? null
      : severities.reduce((a, b) => a + b, 0) / severities.length;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <KindIcon kind={kind} className="h-4 w-4" />
          {KIND_LABEL[kind]} summary, last {days} days
        </CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          <Stat label="Total events" value={String(total)} />
          <Stat label="Peak day" value={String(peak)} />
          <Stat label="7-day avg (today)" value={latest7.toFixed(2).replace(/\.00$/, '')} />
          <Stat label="28-day avg (today)" value={latest28.toFixed(2).replace(/\.00$/, '')} />
          {meanSev != null && (
            <Stat label="Mean severity" value={meanSev.toFixed(2).replace(/\.00$/, '')} />
          )}
        </dl>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-lg font-semibold tabular-nums">{value}</span>
    </div>
  );
}
